import useSWR from "swr";
import { create } from "zustand";

import { getSWRKey, SwrStore, useInterval } from "~utils";

import { AnalyticsEventType, useAnalyticsEvent } from "../analytics-event";
import { usePromptLimit } from "../prompt-limit";
import { useUser } from "../user";
import {
  isRecentlyModified,
  minEngagedBoardElements,
  useBoardElementCount,
  useBoardLastModified,
} from "../view-board";
import { useLastPromptSignInPersistence } from "./use-last-prompt-sign-in-persistence";

export function usePromptSignIn(): usePromptSignInReturn {
  const { shouldPromptDate } = useStore();
  const { submitEvent } = useAnalyticsEvent();
  const { user } = useUser();

  const acceptPrompt = (): void => {
    submitEvent({ type: AnalyticsEventType.AcceptPromptSignIn });
  };

  // The store outlives the prompt: it is module-global and the page remounts on every
  // navigation back to the board. Signing in has to retract a prompt already issued,
  // or the user is told to sign in right after doing so.
  return {
    shouldPromptDate: user.authenticated ? undefined : shouldPromptDate,
    acceptPrompt,
  };
}

export type usePromptSignInReturn = {
  shouldPromptDate?: Date;
  acceptPrompt: () => void;
};

export function usePromptSignInEffect(): void {
  const { canPrompt, reportPrompted } = usePromptLimit();
  const { lastPrompted, setLastPrompted } = useLastSignInPrompted();
  const { user } = useUser();
  const { shouldPromptDate, setShouldPromptDate } = useStore();
  const { submitEvent } = useAnalyticsEvent();

  const elementCount = useBoardElementCount();
  const boardLastModified = useBoardLastModified();

  useInterval((): void => {
    if (user.authenticated) return;
    if (!isBoardWorthKeeping(elementCount, boardLastModified)) return;
    if (hasRecentlyPrompted(lastPrompted)) return;
    if (!canPrompt) return;
    // The persisted read only catches up a render or two after it is written, and the
    // interval would fire again in between. Hold the same window locally to cover that.
    if (shouldPromptDate !== undefined && hasRecentlyPrompted(shouldPromptDate))
      return;

    const now = new Date();
    setShouldPromptDate(now);
    setLastPrompted(now);
    reportPrompted(now);

    submitEvent({
      type: AnalyticsEventType.PromptSignIn,
    });
  });
}

function useLastSignInPrompted(): useLastSignInPromptedReturn {
  const {
    getLastPrompted: getLastPromptedPersisted,
    setLastPrompted: setLastPromptedPersisted,
  } = useLastPromptSignInPersistence();

  const { data, mutate } = useSWR(
    getSWRKey(SwrStore.LastSignInPrompted),
    getLastPromptedPersisted,
    { suspense: true },
  );

  const setLastPrompted = (date: Date): void =>
    void mutate(() => setLastPromptedPersisted(date).then(() => date), {
      optimisticData: date,
      revalidate: false,
    });

  return { lastPrompted: data ?? new Date(0), setLastPrompted };
}

type useLastSignInPromptedReturn = {
  lastPrompted: Date;
  setLastPrompted: (date: Date) => void;
};

// What earns the right to ask is a board worth keeping, not how long the user has been
// around: the pitch is "keep your boards", so it only lands once there is one to lose.
// The lull matters too — crossing the threshold is itself an edit, and the prompt would
// otherwise land mid-drag as the sixth element goes down.
function isBoardWorthKeeping(
  elementCount: number | undefined,
  boardLastModified: Date | undefined,
): boolean {
  if (elementCount === undefined || elementCount <= minEngagedBoardElements)
    return false;

  if (boardLastModified === undefined) return false;

  return !isRecentlyModified(boardLastModified);
}

function hasRecentlyPrompted(lastPrompted: Date): boolean {
  const targetDate = new Date(lastPrompted.getTime() + weekMs);
  return new Date() < targetDate;
}

const weekMs = 1000 * 60 * 60 * 24 * 7;

const useStore = create<StoreState>((set) => ({
  setShouldPromptDate: (shouldPromptDate: Date) => set({ shouldPromptDate }),
}));

type StoreState = {
  shouldPromptDate?: Date;
  setShouldPromptDate: (date: Date) => void;
};
